import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  format, addDays, subDays, startOfWeek,
  addWeeks, subWeeks, differenceInCalendarWeeks,
  startOfMonth, endOfMonth, addMonths, subMonths,
} from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  ChevronLeft, ChevronRight, Coffee, Plus, FileText,
  BookOpen, BookMarked, Users, Settings2, Check, CalendarDays, X, Pencil, Brain, ClipboardList, CheckCircle2,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListTimetables, useGetTimetable,
  useGetDiaryEntries, useSaveDiarySlot, useSaveDiaryDayNotes,
  useListRecords, useListStudents, useListTasks, useUpdateTask,
  useListCalendarEvents, useCreateCalendarEvent, useUpdateCalendarEvent, useDeleteCalendarEvent,
  useGetDailyOverride, useListDailyOverrides, useGetProfile,
  useListClasses, useListCourses,
  getListTasksQueryKey, getListTimetablesQueryKey, getGetDiaryEntriesQueryKey,
  type Timetable, type Profile, type DailyOverride,
} from "@workspace/api-client-react";
import { MonthView } from "@/components/diary-month-view";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocation } from "wouter";
import { useDiary } from "@/contexts/DiaryContext";
import { Badge } from "@/components/ui/badge";
import { RichEditor } from "@/components/rich-editor";
import { RecordFormPanel } from "@/components/record-form-panel";
import { Lock, Sparkles } from "lucide-react";
import { LessonPrepDrawer } from "@/components/lesson-prep-drawer";
import { SchoolBadge } from "@/components/school-badge";
import { DataMinimisationTip } from "@/components/DataMinimisationTip";
import { DailyOverrideDrawer, type DraftSlot } from "@/components/daily-override-drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useProgramRegisters, useDiaryProgramLinks, useAddDiaryProgramLink, type ProgramItem, type DiaryProgramLink } from "@/lib/program-registration";
import { ProgramItemPicker } from "@/components/program-registration/program-item-picker";
import { type InsertAPI } from "@/components/rich-editor";
import {
  DeliveryStatusQuickLog,
  type PulledItem,
} from "@/components/program-registration/delivery-status-quick-log";

// ─── Record type metadata (for InlineRecordChip display only) ─────────────────

const CHIP_TYPE_META: Record<string, { label: string; color: string }> = {
  general:   { label: "General",   color: "bg-gray-100 text-gray-700"    },
  nccd:      { label: "NCCD",      color: "bg-blue-100 text-blue-700"    },
  contact:   { label: "Contact",   color: "bg-green-100 text-green-700"  },
  behaviour: { label: "Behaviour", color: "bg-orange-100 text-orange-700" },
};

const DAY_NAMES = ["monday", "tuesday", "wednesday", "thursday", "friday"];

function getDayName(date: Date): string {
  const map: Record<number, string> = {
    1: "monday", 2: "tuesday", 3: "wednesday", 4: "thursday", 5: "friday",
  };
  return map[date.getDay()] ?? "monday";
}

function getWeekIndex(timetableStartDate: string, currentDate: Date): number {
  const start = new Date(timetableStartDate);
  const diff = differenceInCalendarWeeks(currentDate, start, { weekStartsOn: 1 });
  return Math.max(0, diff);
}

// ─── Inline record chip ──────────────────────────────────────────────────────

function InlineRecordChip({ record }: { record: any }) {
  const [, setLocation] = useLocation();
  const meta = CHIP_TYPE_META[record.type] ?? CHIP_TYPE_META.general;
  const adjustments: string[] = (record.fields as any)?.adjustments ?? [];
  const preview = record.shorthandNotes?.slice(0, 80) || record.description?.slice(0, 80) || "";
  const studentNames: string[] = record.studentNames ?? [];

  return (
    <button
      type="button"
      onClick={() => setLocation(`/app/records/new?edit=${record.id}&from=/app/diary`)}
      className="group w-full flex items-start gap-2 px-4 py-2 text-xs border-t border-gray-100 hover:bg-blue-50/50 text-left transition-colors"
     data-write-action>
      <span className={`flex-shrink-0 mt-0.5 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.color}`}>
        {meta.label}
      </span>
      <div className="flex-1 min-w-0 space-y-0.5">
        {studentNames.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {studentNames.map((n, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">
                <Users className="h-2.5 w-2.5" />{n}
              </span>
            ))}
          </div>
        )}
        {adjustments.length > 0 && (
          <div className="text-blue-600 truncate">
            {adjustments.slice(0, 4).join(" · ")}{adjustments.length > 4 ? ` +${adjustments.length - 4}` : ""}
          </div>
        )}
        {preview && <div className="text-gray-500 truncate">{preview}</div>}
      </div>
      <Pencil className="h-3 w-3 flex-shrink-0 mt-0.5 text-gray-300 group-hover:text-blue-400 transition-colors" />
    </button>
  );
}

// ─── PeriodCard ─────────────────────────────────────────────────────────────

function PeriodCard({
  slot,
  date,
  timetableId,
  initialContent,
  initialDiaryEntryId,
  records,
  slotKey: slotKeyOverride,
}: {
  slot: {
    id: number;
    label: string;
    slotType: string;
    courseId?: number | null;
    courseName?: string | null;
    courseColor?: string | null;
    courseMissing?: boolean;
    classId?: number | null;
    className?: string | null;
    classMissing?: boolean;
    fromSchool?: boolean;
  };
  date: Date;
  timetableId: number;
  initialContent: string;
  initialDiaryEntryId?: number | null;
  records: any[];
  slotKey?: string;
}) {
  const [, setLocation] = useLocation();
  const [content, setContent] = useState(initialContent);
  const [saved, setSaved] = useState(false);
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [showPrepDrawer, setShowPrepDrawer] = useState(false);
  const [showProgramPicker, setShowProgramPicker] = useState(false);
  const [pendingInsertHtml, setPendingInsertHtml] = useState<string | null>(null);
  const insertRef = useRef<InsertAPI | null>(null);
  const [diaryEntryId, setDiaryEntryId] = useState<number | null>(initialDiaryEntryId ?? null);
  // Sync diaryEntryId from prop when diary entries finish loading (useState
  // doesn't re-initialize when props change after first mount).
  useEffect(() => {
    if (initialDiaryEntryId != null && diaryEntryId == null) {
      setDiaryEntryId(initialDiaryEntryId);
    }
  }, [initialDiaryEntryId]);
  const addLink = useAddDiaryProgramLink();
  const { data: persistedLinks = [] } = useDiaryProgramLinks(diaryEntryId);
  const pulledItems: PulledItem[] = persistedLinks.map((l: DiaryProgramLink) => ({
    registerId: l.programRegisterId,
    item: l.item,
    existingEvent: l.existingEvent ?? null,
  }));
  const loggedCount = persistedLinks.filter((l: DiaryProgramLink) => l.existingEvent !== null).length;
  const allDeliveryLogged = loggedCount > 0 && loggedCount === persistedLinks.length;
  const [showDeliveryStatus, setShowDeliveryStatus] = useState(false);
  const { data: allRegisters = [] } = useProgramRegisters();
  const hasActiveRegisters = allRegisters.some((r) => r.status !== "archived");
  const queryClient = useQueryClient();
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveDiarySlot = useSaveDiarySlot();
  const slotKey = slotKeyOverride ?? `slot-${slot.id}`;

  const { data: allStudents = [] } = useListStudents();

  // Update content when initial content loads from server
  useEffect(() => {
    setContent(initialContent);
  }, [initialContent]);

  const handleChange = useCallback((html: string) => {
    setContent(html);
    setSaved(false);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const saved = await saveDiarySlot.mutateAsync({
          date: format(date, "yyyy-MM-dd"),
          slotKey,
          data: {
            timetableId,
            courseId: slot.courseId ?? undefined,
            content: html,
          },
        });
        setDiaryEntryId(saved.id);
        // Keep the diary-entries cache current so any remount caused by a
        // Customise save or reset (which changes slot keys and triggers
        // PeriodCard unmount/remount) finds this entry immediately instead of
        // reading stale data that pre-dates the save.
        const ds = format(date, "yyyy-MM-dd");
        queryClient.setQueryData(
          getGetDiaryEntriesQueryKey({ timetableId, startDate: ds, endDate: ds }),
          (old: unknown) => {
            const entries = Array.isArray(old) ? old : [];
            const idx = entries.findIndex((e: { slotKey: string }) => e.slotKey === slotKey);
            return idx >= 0
              ? entries.map((e: unknown, i: number) => (i === idx ? saved : e))
              : [...entries, saved];
          },
        );
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } catch {}
    }, 800);
  }, [date, slotKey, slot.courseId, timetableId, saveDiarySlot, queryClient]);

  const handleProgramInsert = useCallback((html: string) => {
    if (!html) return;
    if (insertRef.current?.isEmpty()) {
      insertRef.current.append(html);
    } else {
      setPendingInsertHtml(html);
    }
  }, []);

  const handleProgramPick = useCallback(async (registerId: number, item: ProgramItem) => {
    // Ensure the slot is saved before persisting the link so we have a diaryEntryId.
    let entryId = diaryEntryId;
    if (!entryId) {
      try {
        const saved = await saveDiarySlot.mutateAsync({
          date: format(date, "yyyy-MM-dd"),
          slotKey,
          data: { timetableId, courseId: slot.courseId ?? undefined, content },
        });
        entryId = saved.id;
        setDiaryEntryId(saved.id);
      } catch {
        return; // abort link if save fails
      }
    }
    try {
      await addLink.mutateAsync({ diaryEntryId: entryId, programRegisterId: registerId, programItemId: item.id });
    } catch {
      // link may already exist — silently ignore
    }
  }, [diaryEntryId, saveDiarySlot, date, slotKey, timetableId, slot.courseId, content, addLink]);

  if (slot.slotType === "break") {
    return (
      <div className="flex items-center justify-center py-3 relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-dashed border-gray-300" />
        </div>
        <span className="relative bg-gray-50 px-4 flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-widest">
          <Coffee className="h-3 w-3" />
          {slot.label}
        </span>
      </div>
    );
  }

  const accentColor = slot.courseColor ?? "#94a3b8";
  const studentCount = allStudents.length;

  return (
    <>
    <Card className="shadow-sm border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      <div className="flex">
        <div className="w-1.5 rounded-l flex-shrink-0" style={{ backgroundColor: accentColor }} />
        <div className="flex-1 min-w-0">
          {/* Period header */}
          <div className="bg-white border-b border-gray-100 px-4 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex-shrink-0 w-20">
                {slot.label}
              </span>
              {slot.courseName ? (
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-bold text-sm truncate" style={{ color: accentColor }}>
                    {slot.courseName}
                  </span>
                  {slot.className ? (
                    <Badge variant="outline" className="text-xs flex-shrink-0">
                      <Users className="h-3 w-3 mr-1" />
                      {slot.className}
                    </Badge>
                  ) : slot.classMissing ? (
                    // Task #142: classId pointed at a hard-deleted class.
                    // Same shape of fix as the courseMissing tag below.
                    <Badge
                      variant="outline"
                      className="text-xs flex-shrink-0 text-red-600 border-red-200 bg-red-50"
                      data-testid="period-deleted-class"
                      title="The class this period pointed at no longer exists. Open Customise today to clear it."
                    >
                      (deleted class)
                    </Badge>
                  ) : null}
                  {slot.fromSchool && <SchoolBadge />}
                </div>
              ) : slot.className ? (
                // Class-only period: no subject assigned, show the class badge directly.
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className="text-xs flex-shrink-0">
                    <Users className="h-3 w-3 mr-1" />
                    {slot.className}
                  </Badge>
                  {slot.fromSchool && <SchoolBadge />}
                </div>
              ) : slot.courseMissing ? (
                // Task #103: a courseId was saved against this period but the
                // course no longer exists (legacy data or hard-delete). Make it
                // visible instead of rendering a silent blank — teachers can
                // open "Customise today" to clear it.
                <span
                  className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
                  data-testid="period-deleted-course"
                  title="The course this period pointed at no longer exists. Open Customise today to clear it."
                >
                  (deleted course)
                </span>
              ) : (
                <span className="text-sm text-gray-400 italic">No subject assigned</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
              {saved && (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <Check className="h-3 w-3" /> Saved
                </span>
              )}
              <Button
                variant="ghost"
                size="sm"
                className={`h-7 gap-1.5 text-xs transition-colors ${showRecordForm ? "text-primary bg-primary/5" : "text-gray-500 hover:text-primary hover:bg-primary/5"}`}
                onClick={() => setShowRecordForm((v) => !v)}
               data-write-action>
                {showRecordForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                Record
              </Button>
              {hasActiveRegisters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                  title="Pull from program"
                  onClick={() => setShowProgramPicker(true)}
                  data-write-action
                >
                  <BookOpen className="h-3.5 w-3.5" />
                  Program
                </Button>
              )}
              {pulledItems.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-7 gap-1.5 text-xs transition-colors ${
                    allDeliveryLogged
                      ? "text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                      : "text-blue-600 hover:bg-blue-50"
                  }`}
                  title={allDeliveryLogged ? "Delivery logged — click to review or update" : "Log delivery status"}
                  onClick={() => setShowDeliveryStatus(true)}
                  data-write-action
                >
                  {allDeliveryLogged ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : (
                    <ClipboardList className="h-3.5 w-3.5" />
                  )}
                  {persistedLinks.length > 1 && (
                    <span className="text-xs font-medium tabular-nums">
                      {loggedCount}/{persistedLinks.length}
                    </span>
                  )}
                  Delivery
                </Button>
              )}
              {slot.courseId && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 text-xs text-gray-500 hover:text-primary hover:bg-primary/5 transition-colors"
                  title="View lesson plans"
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (slot.classId) params.set("classId", String(slot.classId));
                    if (slot.courseId) params.set("courseId", String(slot.courseId));
                    params.set("date", format(date, "yyyy-MM-dd"));
                    setLocation(`/app/lessons?${params.toString()}`);
                  }}
                >
                  <BookMarked className="h-3.5 w-3.5" />
                  Lessons
                </Button>
              )}
              {slot.classId && import.meta.env.VITE_LESSON_PREP_ENABLED === "true" && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1.5 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 transition-colors"
                  title="AI lesson prep"
                  onClick={() => setShowPrepDrawer(true)}
                 data-write-action>
                  <Brain className="h-3.5 w-3.5" />
                  Prep
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-gray-400"
                onClick={() => setLocation("/app/timetable-settings")}
                title="Edit timetable"
              >
                <Settings2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Rich editor */}
          <RichEditor
            content={content}
            onChange={handleChange}
            placeholder={slot.courseName ? `Lesson plan for ${slot.courseName}…` : "Lesson plan…"}
            minHeight={140}
            insertRef={insertRef}
          />
          <DataMinimisationTip
            className="mt-1.5"
            text="Tip: lesson plans are about teaching, not students — keep individual student detail out and use the records page for adjustments."
          />

          {/* Existing records for this period */}
          {records.length > 0 && (
            <div className="divide-y divide-gray-50">
              {records.map((r: any) => <InlineRecordChip key={r.id} record={r} />)}
            </div>
          )}

          {/* Inline record form */}
          {showRecordForm && (
            <RecordFormPanel
              date={date}
              slot={slot}
              classStudents={allStudents as any[]}
              onSaved={() => setShowRecordForm(false)}
              onCancel={() => setShowRecordForm(false)}
            />
          )}
        </div>
      </div>
    </Card>

    {/* Lesson Prep drawer (Premium) */}
    {slot.classId && import.meta.env.VITE_LESSON_PREP_ENABLED === "true" && (
      <LessonPrepDrawer
        classId={slot.classId}
        className={slot.className}
        courseName={slot.courseName}
        date={date}
        open={showPrepDrawer}
        onOpenChange={setShowPrepDrawer}
      />
    )}

    <ProgramItemPicker
      open={showProgramPicker}
      onClose={() => setShowProgramPicker(false)}
      onInsert={handleProgramInsert}
      onPickItem={handleProgramPick}
    />
    <DeliveryStatusQuickLog
      open={showDeliveryStatus}
      onClose={() => setShowDeliveryStatus(false)}
      pulledItems={pulledItems}
      diaryEntryId={diaryEntryId}
      slotLabel={slot.label}
      slotClassId={slot.classId ?? null}
    />

    {/* Overwrite-protection dialog — shown when editor already has content */}
    <AlertDialog
      open={pendingInsertHtml !== null}
      onOpenChange={(v) => { if (!v) setPendingInsertHtml(null); }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Add to existing content?</AlertDialogTitle>
          <AlertDialogDescription>
            This diary entry already has content. Would you like to add the program item content below it, or replace it?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-3">
          <AlertDialogCancel onClick={() => setPendingInsertHtml(null)}>
            Cancel
          </AlertDialogCancel>
          <Button
            variant="outline"
            onClick={() => {
              if (pendingInsertHtml) insertRef.current?.replace(pendingInsertHtml);
              setPendingInsertHtml(null);
            }}
          >
            Replace current content
          </Button>
          <AlertDialogAction
            onClick={() => {
              if (pendingInsertHtml) insertRef.current?.append(pendingInsertHtml);
              setPendingInsertHtml(null);
            }}
          >
            Add below
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
}

// ─── DueTasksCard ────────────────────────────────────────────────────────────

function DueTasksCard({ date }: { date: Date }) {
  const { data: tasks } = useListTasks();
  const updateTask = useUpdateTask();
  const queryClient = useQueryClient();

  const dueTasks = (tasks ?? []).filter(
    (t) => !t.completed && t.dueDate === format(date, "yyyy-MM-dd")
  );

  if (dueTasks.length === 0) return null;

  const toggle = (task: any) =>
    updateTask.mutate({ taskId: task.id, data: { ...task, completed: true } }, {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getListTasksQueryKey() }),
    });

  return (
    <Card className="shadow-sm border-amber-200 overflow-hidden">
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center gap-2 rounded-t-xl">
        <Check className="h-4 w-4 text-amber-600" />
        <h3 className="font-semibold text-amber-700">Due Today</h3>
        <Badge variant="outline" className="ml-auto border-amber-300 text-amber-600 text-xs">
          {dueTasks.length}
        </Badge>
      </div>
      <CardContent className="p-0">
        {dueTasks.map((task, i) => (
          <div
            key={task.id}
            className={`flex items-center gap-3 px-4 py-2.5 ${i < dueTasks.length - 1 ? "border-b border-amber-100" : ""}`}
          >
            <button onClick={() => toggle(task)} className="flex-shrink-0">
              <div className="h-4 w-4 rounded border-2 border-amber-300 hover:border-amber-500 hover:bg-amber-50 transition-colors" />
            </button>
            <span className="text-sm text-gray-800">{task.title}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ─── DayNotesCard ────────────────────────────────────────────────────────────

function DayNotesCard({
  date,
  timetableId,
  initialNotes,
  events = [],
}: {
  date: Date;
  timetableId: number;
  initialNotes: string;
  events?: { id: number; title: string; color: string }[];
}) {
  const [notes, setNotes] = useState(initialNotes);
  const [saved, setSaved] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveDayNotes = useSaveDiaryDayNotes();

  useEffect(() => { setNotes(initialNotes); }, [initialNotes]);

  const handleChange = (html: string) => {
    setNotes(html);
    setSaved(false);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await saveDayNotes.mutateAsync({
          date: format(date, "yyyy-MM-dd"),
          data: { timetableId, notes: html },
        });
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } catch {}
    }, 800);
  };

  return (
    <Card className="shadow-sm border-gray-200 overflow-hidden">
      <div className="bg-blue-50 border-b px-4 py-2.5 flex items-center justify-between rounded-t-xl">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-primary">Day Notes</h3>
        </div>
        {saved && (
          <span className="flex items-center gap-1 text-xs text-green-600">
            <Check className="h-3 w-3" /> Saved
          </span>
        )}
      </div>

      {/* Events for this day */}
      {events.length > 0 && (
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/50 flex flex-wrap gap-2">
          {events.map((ev) => (
            <span
              key={ev.id}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border"
              style={{ backgroundColor: ev.color + "18", color: ev.color, borderColor: ev.color + "40" }}
            >
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: ev.color }} />
              {ev.title}
            </span>
          ))}
        </div>
      )}

      <CardContent className="p-0">
        <RichEditor
          content={notes}
          onChange={handleChange}
          placeholder="General notes for the day…"
          minHeight={80}
        />
      </CardContent>
    </Card>
  );
}

// ─── WeekDayColumn ──────────────────────────────────────────────────────────

interface RawSlot {
  id: number;
  label: string;
  slotType: string;
  day: string;
  position: number;
  courseId?: number | null;
  courseName?: string | null;
  courseColor?: string | null;
  courseMissing?: boolean;
  classId?: number | null;
  className?: string | null;
  classMissing?: boolean;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

// ─── WeekSlotNote ────────────────────────────────────────────────────────────

function WeekSlotNote({
  slot,
  date,
  timetableId,
  initialContent,
  slotKeyOverride,
}: {
  slot: RawSlot;
  date: Date;
  timetableId: number;
  initialContent: string;
  /**
   * Optional override for the slotKey written to the diary_entries row. The
   * day view uses `override-{position}` for slots coming from a daily
   * override; the week view must use the same key so notes saved in either
   * view are visible from the other.
   */
  slotKeyOverride?: string;
}) {
  const [content, setContent] = useState(initialContent);
  const [saved, setSaved] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveDiarySlot = useSaveDiarySlot();

  useEffect(() => { setContent(initialContent); }, [initialContent]);

  const slotKey = slotKeyOverride ?? `slot-${slot.id}`;

  const handleChange = useCallback((html: string) => {
    setContent(html);
    setSaved(false);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await saveDiarySlot.mutateAsync({
          date: format(date, "yyyy-MM-dd"),
          slotKey,
          data: {
            timetableId,
            courseId: slot.courseId ?? undefined,
            content: html,
          },
        });
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } catch {}
    }, 800);
  }, [date, slotKey, slot.courseId, timetableId, saveDiarySlot]);

  return (
    <div className="relative">
      {saved && (
        <span className="absolute top-1 right-1 z-10 flex items-center gap-0.5 text-[10px] text-green-600 bg-white/80 rounded px-1">
          <Check className="h-2.5 w-2.5" /> Saved
        </span>
      )}
      <RichEditor
        content={content}
        onChange={handleChange}
        placeholder={slot.courseName ? `Notes for ${slot.courseName}…` : "Lesson notes…"}
        minHeight={60}
        compact
      />
    </div>
  );
}

function WeekDayColumn({
  date,
  slots,
  timetableId,
  dayNotes = "",
  events = [],
  getSlotContent,
  override,
}: {
  date: Date;
  slots: RawSlot[];
  timetableId: number;
  dayNotes?: string;
  events?: { id: number; title: string; color: string }[];
  getSlotContent: (slotKey: string) => string;
  override?: DailyOverride;
}) {
  const dayName = getDayName(date);
  const isToday = format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

  // When an override exists for this day, replace the base timetable's slots
  // entirely. Synthesise ids large enough not to collide with real
  // timetable_slots ids; the actual slotKey written to diary_entries is
  // `override-{position}` (computed below) so notes stay consistent with
  // the day view, which uses the same convention.
  const daySl: RawSlot[] = override
    ? override.slots.map((s) => ({
        id: 1_000_000 + s.position,
        label: s.label,
        slotType: s.slotType,
        day: dayName,
        position: s.position,
        courseId: s.courseId ?? null,
        courseName: s.courseName ?? null,
        courseColor: s.courseColor ?? null,
        courseMissing: s.courseMissing ?? false,
        classId: s.classId ?? null,
        className: s.className ?? null,
        classMissing: s.classMissing ?? false,
      }))
    : slots.filter((s) => s.day === dayName).sort((a, b) => a.position - b.position);
  const plainNotes = stripHtml(dayNotes);

  return (
    <div className="flex-1 min-w-0" data-day-column={format(date, "yyyy-MM-dd")}>
      {/* Day header */}
      <div className={`text-center py-2 rounded-lg mb-2 ${isToday ? "bg-primary text-white" : "bg-gray-100 text-gray-600"}`}>
        <div className="text-xs font-medium uppercase tracking-wider">{format(date, "EEE")}</div>
        <div className="text-lg font-bold">{format(date, "d")}</div>
        {override && (
          <div
            className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${isToday ? "bg-white/20 text-white" : "bg-amber-100 text-amber-800"}`}
            data-testid="week-day-customised-badge"
          >
            <Sparkles className="h-2.5 w-2.5" />
            Customised
          </div>
        )}
      </div>

      {/* Events */}
      {events.length > 0 && (
        <div className="mb-2 space-y-1">
          {events.map((ev) => (
            <div
              key={ev.id}
              className="text-xs font-medium px-2 py-1 rounded truncate"
              style={{ backgroundColor: ev.color + "1a", color: ev.color }}
            >
              {ev.title}
            </div>
          ))}
        </div>
      )}

      {/* Day notes preview */}
      {plainNotes && (
        <div className="mb-2 px-2 py-1.5 bg-blue-50 rounded-lg border border-blue-100">
          <div className="flex items-center gap-1 text-xs text-primary font-medium mb-0.5">
            <FileText className="h-3 w-3" /> Notes
          </div>
          <p className="text-xs text-gray-600 line-clamp-2">{plainNotes}</p>
        </div>
      )}

      {/* Timetable slots */}
      <div className="space-y-2">
        {daySl.length === 0 ? (
          <div className="text-center text-xs text-gray-400 py-4">No slots</div>
        ) : (
          daySl.map((slot) => (
            <div key={slot.id} className="text-xs">
              {slot.slotType === "break" ? (
                <div className="text-center text-gray-400 py-1 border-t border-dashed border-gray-200">
                  <Coffee className="h-3 w-3 inline" /> {slot.label}
                </div>
              ) : (
                <div
                  className="rounded border overflow-hidden"
                  style={{
                    borderColor: slot.courseColor ?? "#e2e8f0",
                  }}
                >
                  {/* Slot header */}
                  <div
                    className="px-2 py-1.5 flex items-center gap-1.5"
                    style={{ backgroundColor: slot.courseColor ? `${slot.courseColor}18` : "#f8fafc" }}
                  >
                    <span className="font-semibold text-gray-600">{slot.label}</span>
                    {slot.courseName ? (
                      <span className="font-bold truncate" style={{ color: slot.courseColor ?? "#64748b" }}>
                        {slot.courseName}
                      </span>
                    ) : slot.courseMissing ? (
                      // Task #103: same "(deleted course)" flag the day view
                      // uses, scaled down for the compact week column.
                      <span
                        className="font-medium text-red-600 bg-red-50 border border-red-200 rounded px-1 py-0 text-[10px]"
                        data-testid="week-period-deleted-course"
                      >
                        (deleted course)
                      </span>
                    ) : null}
                    {slot.className ? (
                      <span className="text-gray-400 truncate ml-auto">{slot.className}</span>
                    ) : slot.classMissing ? (
                      // Task #142: same shape as the courseMissing tag — make
                      // a stale classId visible in the compact week column.
                      <span
                        className="font-medium text-red-600 bg-red-50 border border-red-200 rounded px-1 py-0 text-[10px] ml-auto"
                        data-testid="week-period-deleted-class"
                      >
                        (deleted class)
                      </span>
                    ) : null}
                  </div>
                  {/* Inline note editor */}
                  {(() => {
                    const slotKey = override
                      ? `override-${slot.position}`
                      : `slot-${slot.id}`;
                    return (
                      <WeekSlotNote
                        slot={slot}
                        date={date}
                        timetableId={timetableId}
                        initialContent={getSlotContent(slotKey)}
                        slotKeyOverride={override ? slotKey : undefined}
                      />
                    );
                  })()}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Main Diary page ─────────────────────────────────────────────────────────

export default function Diary() {
  const [view, setView] = useState<"day" | "week" | "month">("day");
  const canWeek = true;
  const canMonth = true;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [promptDismissed, setPromptDismissed] = useState(false);
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const { data: profile } = useGetProfile();
  const isSupportStaff = (profile as Profile | undefined)?.schoolRole === "support_staff";

  const { data: timetables, isLoading: isTimetablesLoading } = useListTimetables();
  const { activeTimetableId, setActiveTimetableId } = useDiary();
  const timetableList = (timetables ?? []) as Timetable[];
  const activeTimetable: Timetable | undefined =
    timetableList.find((t) => t.id === activeTimetableId) ?? timetableList[0];
  const { data: timetableDetail, isLoading: isTimetableLoading } = useGetTimetable(
    activeTimetable?.id ?? 0,
    { query: { enabled: !!activeTimetable?.id } }
  );

  // Pull classes/courses with includeArchived so historical timetable slots
  // that point at a year-end-archived class/course can render with an
  // "(archived)" suffix in the diary (mirrors the lessons.tsx pattern).
  const { data: allClassesForArchive = [] } = useListClasses({ includeArchived: "1" });
  const { data: allCoursesForArchive = [] } = useListCourses({ includeArchived: "1" });
  const archivedClassIds = useMemo(
    () => new Set(allClassesForArchive.filter((c) => c.archived).map((c) => c.id)),
    [allClassesForArchive],
  );
  const archivedCourseIds = useMemo(
    () => new Set(allCoursesForArchive.filter((c) => c.archived).map((c) => c.id)),
    [allCoursesForArchive],
  );
  const enrichSlotWithArchived = useCallback(
    <S extends { classId?: number | null; className?: string | null; courseId?: number | null; courseName?: string | null }>(slot: S): S => ({
      ...slot,
      className:
        slot.classId && slot.className && archivedClassIds.has(slot.classId)
          ? `${slot.className} (archived)`
          : slot.className,
      courseName:
        slot.courseId && slot.courseName && archivedCourseIds.has(slot.courseId)
          ? `${slot.courseName} (archived)`
          : slot.courseName,
    }),
    [archivedClassIds, archivedCourseIds],
  );

  const dateStr = format(currentDate, "yyyy-MM-dd");

  // Per-day override (always fetched so we know whether to render base vs override)
  const { data: dailyOverride } = useGetDailyOverride(dateStr);
  const hasOverride = !!dailyOverride;

  // Compute week dates early so hooks can use them
  const weekStartDate = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDaysArr = Array.from({ length: 5 }, (_, i) => addDays(weekStartDate, i));
  const weekStartStr = format(weekStartDate, "yyyy-MM-dd");
  const weekEndStr = format(weekDaysArr[4], "yyyy-MM-dd");

  // Load diary entries for current day (day view)
  const { data: diaryEntries } = useGetDiaryEntries(
    {
      timetableId: activeTimetable?.id ?? 0,
      startDate: dateStr,
      endDate: dateStr,
    },
    { query: { enabled: view === "day" && !!activeTimetable?.id } }
  );


  // Load diary entries for the full week (week view day notes)
  const { data: weekDiaryEntries = [] } = useGetDiaryEntries(
    {
      timetableId: activeTimetable?.id ?? 0,
      startDate: weekStartStr,
      endDate: weekEndStr,
    },
    { query: { enabled: view === "week" && !!activeTimetable?.id } }
  );

  // Load all per-day overrides for the visible week so the week view can
  // render customised days in place of the base timetable. Single round-trip
  // covering Mon–Fri rather than five per-day requests.
  const { data: weekOverrides = [] } = useListDailyOverrides(
    { startDate: weekStartStr, endDate: weekEndStr },
    { query: { enabled: view === "week" } },
  );

  const weekOverridesByDate = useMemo(() => {
    const map = new Map<string, DailyOverride>();
    for (const ov of weekOverrides) map.set(ov.date, ov);
    return map;
  }, [weekOverrides]);

  // Load records for the current date (to show inline in period cards)
  const { data: dayRecords = [] } = useListRecords(
    { startDate: dateStr, endDate: dateStr },
    { query: { enabled: view === "day" && !!activeTimetable?.id } }
  );

  // Build lookup: period label → records[]
  const recordsByPeriod = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const r of dayRecords as any[]) {
      if (r.period) {
        if (!map.has(r.period)) map.set(r.period, []);
        map.get(r.period)!.push(r);
      }
    }
    return map;
  }, [dayRecords]);

  // Week day-notes lookup: dateStr → raw HTML
  const weekDayNotesMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of weekDiaryEntries as any[]) {
      if (e.slotKey === "day_notes" && e.dayNotes) map.set(e.date, e.dayNotes);
    }
    return map;
  }, [weekDiaryEntries]);

  // Week slot content lookup: `${dateStr}_${slotKey}` → HTML content
  const weekSlotContentMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of weekDiaryEntries as any[]) {
      if (e.slotKey !== "day_notes" && e.content) {
        map.set(`${e.date}_${e.slotKey}`, e.content);
      }
    }
    return map;
  }, [weekDiaryEntries]);

  // Month range for month view event loading
  const monthStart = format(startOfMonth(currentDate), "yyyy-MM-dd");
  const monthEnd = format(endOfMonth(currentDate), "yyyy-MM-dd");

  // Calendar events — different ranges for each view
  const eventsStart = view === "month" ? monthStart : view === "week" ? weekStartStr : dateStr;
  const eventsEnd   = view === "month" ? monthEnd   : view === "week" ? weekEndStr   : dateStr;
  const { data: calendarEvents = [] } = useListCalendarEvents(
    { startDate: eventsStart, endDate: eventsEnd },
    { query: { enabled: !!(eventsStart && eventsEnd) } },
  );

  // Build day events lookup (used by day + week views): dateStr → events[]
  const dayEventsMap = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const ev of calendarEvents as any[]) {
      if (!map.has(ev.date)) map.set(ev.date, []);
      map.get(ev.date)!.push(ev);
    }
    return map;
  }, [calendarEvents]);
  const invalidateEvents = () =>
    queryClient.invalidateQueries({ queryKey: ["/api/events"] });
  const createEvent = useCreateCalendarEvent({
    mutation: { onSuccess: invalidateEvents },
  });
  const updateEvent = useUpdateCalendarEvent({
    mutation: { onSuccess: invalidateEvents },
  });
  const deleteEvent = useDeleteCalendarEvent({
    mutation: { onSuccess: invalidateEvents },
  });

  const navigatePrev = () => {
    if (view === "day") setCurrentDate(subDays(currentDate, 1));
    else if (view === "week") setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subMonths(currentDate, 1));
  };
  const navigateNext = () => {
    if (view === "day") setCurrentDate(addDays(currentDate, 1));
    else if (view === "week") setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addMonths(currentDate, 1));
  };
  const navigateToday = () => setCurrentDate(new Date());

  // ─── Slot-derived data — must stay ABOVE all early returns ───────────────────
  // React requires hooks to be called the same number of times on every render.
  // These useMemo calls were previously placed after the isTimetablesLoading /
  // !activeTimetable guards, which caused "Rendered more hooks than during the
  // previous render" crashes whenever the loading guard fired (first render).
  const weekIndex = activeTimetable
    ? getWeekIndex(activeTimetable.startDate, currentDate)
    : 0;
  const weeks = timetableDetail?.weeks ?? [];
  const weekCount = weeks.length;
  const activeWeek = weekCount > 0 ? weeks[weekIndex % weekCount] : null;
  const dayName = getDayName(currentDate);
  const isWeekendDay = currentDate.getDay() === 0 || currentDate.getDay() === 6;
  // Memoize so the array reference is stable — draftSlotsForDrawer depends on
  // these and passes them as `initialSlots` to the drawer. Without memoization,
  // every parent re-render (e.g. background React Query refetch) creates a new
  // array, which would trigger the drawer's useEffect and reset in-progress edits.
  const allSlots = useMemo(
    () => (activeWeek?.slots ?? []).map(enrichSlotWithArchived),
    // enrichSlotWithArchived is useCallback-stable; activeWeek comes from
    // timetableDetail which only changes when the server responds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeWeek, enrichSlotWithArchived],
  );
  const baseDaySlots = useMemo(
    () =>
      isWeekendDay
        ? []
        : allSlots.filter((s) => s.day === dayName).sort((a, b) => a.position - b.position),
    [isWeekendDay, allSlots, dayName],
  );
  // Initial draft for the override drawer — pre-populates from existing override
  // when present, otherwise from the base day's slots so editing feels natural.
  // Task #103: pass `courseMissing` from the server response into the drawer's
  // draft so the per-period picker can flag broken references and a top-of-
  // drawer banner can offer to clear them in one tap.
  // Memoized so the reference is stable across parent re-renders: the drawer's
  // useEffect seeds its local state from this prop on open, and an unstable
  // reference would reset in-progress edits on every background refetch.
  const draftSlotsForDrawer = useMemo<DraftSlot[]>(
    () =>
      hasOverride
        ? (dailyOverride?.slots ?? []).map((s) => ({
            position: s.position,
            slotType: s.slotType,
            label: s.label,
            courseId: s.courseId ?? null,
            classId: s.classId ?? null,
            courseMissing: s.courseMissing ?? false,
            classMissing: s.classMissing ?? false,
          }))
        : baseDaySlots.map((s, i) => ({
            position: i + 1,
            slotType: s.slotType as "period" | "break",
            label: s.label,
            courseId: s.courseId ?? null,
            classId: s.classId ?? null,
            courseMissing: false,
            classMissing: false,
          })),
    // dailyOverride reference only changes when the server returns new data;
    // baseDaySlots is already memoized above.
    [hasOverride, dailyOverride, baseDaySlots],
  );
  // When an override is active for the current day, existing diary notes may
  // have been written under the BASE timetable's `slot-{id}` key before the
  // override was created. After the override is saved the day-view switches to
  // `override-{position}` keys, so those notes appear missing. Build a
  // position-aligned fallback: override-{n} → slot-{baseDaySlots[n-1].id}.
  // The PeriodCard still WRITES under the override key; this is read-only
  // fallback display so pre-existing notes remain visible without any data
  // migration or diary-entry deletion.
  const overrideToBaseKey = useMemo((): Map<string, string> => {
    if (!hasOverride) return new Map();
    return new Map(
      baseDaySlots.map((s, i) => [`override-${i + 1}`, `slot-${s.id}`] as const),
    );
  }, [hasOverride, baseDaySlots]);

  if (isTimetablesLoading) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!activeTimetable) {
    if (isSupportStaff) {
      return (
        <div className="p-8 max-w-4xl mx-auto text-center mt-20">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-12">
            <Sparkles className="h-12 w-12 text-primary/40 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No periods set up yet</h2>
            <p className="text-gray-600 mb-6">
              Set up the periods you'll be working today — you can change them any day.
            </p>
            <Button
              size="lg"
              className="gap-2"
              onClick={() => {
                setOverrideOpen(true);
              }}
            >
              <Pencil className="h-4 w-4" />
              Set up today's periods
            </Button>
          </div>

          <DailyOverrideDrawer
            open={overrideOpen}
            onOpenChange={(o) => {
              setOverrideOpen(o);
              if (!o) {
                // After saving, the server may have auto-created a timetable.
                // Refresh the timetables list and let the diary remount with it.
                queryClient
                  .invalidateQueries({ queryKey: getListTimetablesQueryKey() })
                  .then(() => {
                    queryClient
                      .fetchQuery<Timetable[]>({ queryKey: getListTimetablesQueryKey() })
                      .then((res) => {
                        const first = res?.[0];
                        if (first?.id && !activeTimetableId) setActiveTimetableId(first.id);
                      })
                      .catch(() => {});
                  });
              }
            }}
            date={currentDate}
            initialSlots={
              hasOverride
                ? (dailyOverride?.slots ?? []).map((s) => ({
                    position: s.position,
                    slotType: s.slotType as "period" | "break",
                    label: s.label,
                    courseId: s.courseId ?? null,
                    classId: s.classId ?? null,
                    courseMissing: s.courseMissing ?? false,
                    classMissing: s.classMissing ?? false,
                  }))
                : []
            }
            hasOverride={hasOverride}
          />
        </div>
      );
    }
    return (
      <div className="p-8 max-w-4xl mx-auto text-center mt-20">
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-12">
          <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Diary Selected</h2>
          <p className="text-gray-600 mb-6">Create a diary for the current term to start planning your lessons.</p>
          <Button onClick={() => setLocation("/app/diaries")}>Go to Diaries</Button>
        </div>
      </div>
    );
  }

  // Build the visible day slots — the per-day override (when present) replaces
  // the base timetable for the current date. We synthesize ids large enough not
  // to collide with real timetable_slots ids and use a stable `override-{pos}`
  // slotKey so saved diary content doesn't mix with base timetable content.
  type BaseDaySlot = (typeof baseDaySlots)[number];
  type ViewSlot = BaseDaySlot & { _slotKey?: string; courseMissing?: boolean; classMissing?: boolean };
  const daySlots: ViewSlot[] = hasOverride
    ? (dailyOverride?.slots ?? []).map((s): ViewSlot => ({
        ...enrichSlotWithArchived({
          id: 1_000_000 + s.position,
          weekId: 0,
          day: dayName as BaseDaySlot["day"],
          position: s.position,
          slotType: s.slotType,
          label: s.label,
          courseId: s.courseId ?? null,
          courseName: s.courseName ?? null,
          courseColor: s.courseColor ?? null,
          classId: s.classId ?? null,
          className: s.className ?? null,
          _slotKey: `override-${s.position}`,
        }),
        // Task #103: carry the server's "the course this slot pointed at no
        // longer exists" flag through the synthesized day-slot so PeriodCard
        // can render the "(deleted course)" tag.
        courseMissing: s.courseMissing ?? false,
        // Task #142: same shape for stale classId references.
        classMissing: s.classMissing ?? false,
      }))
    : baseDaySlots;

  // Records for today whose period doesn't match any visible slot label,
  // or records with no period set — shown in a catch-all section below the slots.
  const unmatchedRecords = useMemo(() => {
    const slotLabels = new Set(daySlots.map((s) => s.label));
    return (dayRecords as any[]).filter((r) => !r.period || !slotLabels.has(r.period));
  }, [daySlots, dayRecords]);

  const weekStart = weekStartDate;
  const weekDays = weekDaysArr;

  // Build lookup: slotKey → entry.
  // For override-active days, also insert override-{n} keys mapped to the
  // corresponding base-slot entry, so PeriodCards find their pre-override notes
  // via a single entryMap.get(slotKey) look-up — no second fallback lookup
  // needed. When an override-{n} entry already exists in diaryEntries (the user
  // wrote content after Customise was saved), that real entry wins because the
  // base entries are written from diaryEntries first, then overrideToBaseKey only
  // fills in the gaps (if (!entryMap.has(overrideKey))).
  const entryMap = new Map(
    (diaryEntries ?? []).map((e) => [e.slotKey, e])
  );
  overrideToBaseKey.forEach((baseKey, overrideKey) => {
    if (!entryMap.has(overrideKey)) {
      const base = entryMap.get(baseKey);
      if (base) entryMap.set(overrideKey, base);
    }
  });
  const dayNotesEntry = entryMap.get("day_notes");


  // Extracted so the toggle can be placed in two positions (desktop left
  // group / mobile bottom row) without duplicating the onValueChange logic.
  const viewToggle = (
    <ToggleGroup type="single" value={view} onValueChange={(v) => {
      if (!v) return;
      if (v === "week" && !canWeek) { setLocation("/pricing"); return; }
      if (v === "month" && !canMonth) { setLocation("/pricing"); return; }
      setView(v as any);
    }}>
      <ToggleGroupItem value="day">Day</ToggleGroupItem>
      <ToggleGroupItem value="week" className={!canWeek ? "opacity-50" : ""}>
        Week {!canWeek && <Lock className="h-3 w-3 ml-1" />}
      </ToggleGroupItem>
      <ToggleGroupItem value="month" className={!canMonth ? "opacity-50" : ""}>
        Month {!canMonth && <Lock className="h-3 w-3 ml-1" />}
      </ToggleGroupItem>
    </ToggleGroup>
  );

  const isOutsideActiveTimetableRange =
    !!activeTimetable.startDate &&
    !!activeTimetable.endDate &&
    (dateStr < activeTimetable.startDate || dateStr > activeTimetable.endDate);

  if (isOutsideActiveTimetableRange) {
    return (
      <div className="p-8 max-w-4xl mx-auto text-center mt-20">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-10">
          <CalendarDays className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            This date is outside this diary
          </h2>
          <p className="text-gray-700 mb-2">
            <strong>{activeTimetable.name}</strong> runs from{" "}
            {format(new Date(activeTimetable.startDate + "T00:00:00"), "d MMM yyyy")} to{" "}
            {format(new Date(activeTimetable.endDate + "T00:00:00"), "d MMM yyyy")}.
          </p>
          <p className="text-gray-600 mb-6">
            To protect your diary notes, this diary can only be viewed and edited within its date range.
            Choose a date inside this diary, or select a different diary.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => setCurrentDate(new Date(activeTimetable.startDate + "T00:00:00"))}>
              Go to diary start date
            </Button>
            <Button variant="outline" onClick={() => setLocation("/app/diaries")}>
              Choose another diary
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header
          Mobile:  two rows — (1) nav + date, (2) view toggle + action buttons
          Desktop (lg+): single row matching the original layout */}
      <div className="border-b bg-white px-4 sm:px-6 py-3 sticky top-0 z-10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-y-2 lg:gap-y-0">

          {/* ── Left group (desktop) / Row 1 (mobile): nav + date ── */}
          <div className="flex items-center gap-2 lg:gap-4 min-w-0">
            {/* View toggle: desktop only — on mobile it lives in row 2 */}
            <div className="hidden lg:block flex-shrink-0">{viewToggle}</div>

            {/* Prev / Today / Next */}
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-md flex-shrink-0">
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded bg-white shadow-sm" onClick={navigatePrev} aria-label="Previous">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost" size="sm"
                className="h-8 px-3 font-medium text-primary hover:text-primary hover:bg-primary/10"
                onClick={navigateToday}
              >
                Today
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded bg-white shadow-sm" onClick={navigateNext} aria-label="Next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Clickable date → jump-to-date popover */}
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button className="flex flex-col items-start group min-w-0" aria-label="Pick a date">
                  <span className="text-sm sm:text-base lg:text-lg font-bold text-gray-900 flex items-center gap-1.5 group-hover:text-primary transition-colors truncate">
                    {view === "day"
                      ? format(currentDate, "EEE, d MMM yyyy")
                      : view === "week"
                        ? `${format(weekDays[0], "d MMM")} – ${format(weekDays[4], "d MMM yyyy")}`
                        : format(currentDate, "MMMM yyyy")}
                    <CalendarDays className="h-4 w-4 flex-shrink-0 text-gray-300 group-hover:text-primary transition-colors" />
                  </span>
                  {view !== "month" && activeWeek && (
                    <span className="text-xs text-gray-400 truncate">{activeTimetable.name} · Week {activeWeek.weekLabel}</span>
                  )}
                  {view === "month" && (
                    <span className="text-xs text-gray-400 truncate">{activeTimetable.name}</span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={currentDate}
                  onSelect={(date) => {
                    if (date) { setCurrentDate(date); setDatePickerOpen(false); }
                  }}
                  initialFocus
                />
                <div className="border-t p-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-sm"
                    onClick={() => { setCurrentDate(new Date()); setDatePickerOpen(false); }}
                  >
                    Today
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* ── Right group (desktop) / Row 2 (mobile): toggle + action buttons ── */}
          <div className="flex items-center justify-between lg:justify-end gap-2 flex-shrink-0">
            {/* View toggle: mobile only — desktop version lives in left group */}
            <div className="lg:hidden">{viewToggle}</div>

            <div className="flex items-center gap-2">
              {view === "day" && hasOverride && (
                <Badge className="gap-1 bg-amber-100 text-amber-800 border-0 hover:bg-amber-100">
                  <Sparkles className="h-3 w-3" />
                  <span className="hidden sm:inline">Custom</span>
                </Badge>
              )}
              {view === "day" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setOverrideOpen(true)}
                  aria-label={hasOverride ? "Edit today's periods" : "Customise today's periods"}
                  data-write-action>
                  <Pencil className="h-4 w-4" />
                  <span className="hidden sm:inline">{hasOverride ? "Edit today" : "Customise"}</span>
                  <span className="sm:hidden">{hasOverride ? "Edit" : "Customise"}</span>
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setLocation("/app/records/new")}
                aria-label="New Record"
                data-write-action>
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">New Record</span>
                <span className="sm:hidden">Record</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setLocation("/app/timetable-settings")}
                aria-label="Timetable settings">
                <Settings2 className="h-4 w-4 text-gray-500" />
              </Button>
            </div>
          </div>

        </div>
      </div>

      <DailyOverrideDrawer
        open={overrideOpen}
        onOpenChange={setOverrideOpen}
        date={currentDate}
        initialSlots={draftSlotsForDrawer}
        hasOverride={hasOverride}
      />

      {/* Body */}
      <div className="flex-1 overflow-auto bg-gray-50 p-4 sm:p-6">
        {!isTimetablesLoading && timetableList.length === 0 && !promptDismissed ? (
          /* ── First-use welcome panel (no timetable yet) ── */
          <div className="max-w-xl mx-auto mt-16 sm:mt-24 px-4">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm">
              <CalendarDays className="h-12 w-12 text-primary/40 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                Set up your teaching timetable
              </h2>
              <p className="text-sm text-gray-600 mb-3 max-w-sm mx-auto">
                Start by setting up your teaching timetable. This helps ClassTrack organise your
                diary, lesson notes, records and exports. You can use subjects, classes, or both
                depending on how you teach.
              </p>
              <p className="text-xs text-gray-400 mb-7 max-w-sm mx-auto">
                If your timetable changes from day to day, you may prefer to create the subjects
                and/or classes you work with, then customise your diary timetable each day using
                the Customise button.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button onClick={() => setLocation("/app/timetable-settings")}>
                  Set up teaching timetable
                </Button>
                <Button
                  variant="ghost"
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => setPromptDismissed(true)}
                >
                  Skip for now
                </Button>
              </div>
            </div>
          </div>
        ) : isTimetableLoading ? (
          <div className="space-y-4 max-w-5xl mx-auto">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
          </div>
        ) : view === "day" ? (
          <div className="max-w-3xl mx-auto space-y-4">
            <DueTasksCard date={currentDate} />
            <DayNotesCard
              date={currentDate}
              timetableId={activeTimetable.id}
              initialNotes={dayNotesEntry?.dayNotes ?? ""}
              events={dayEventsMap.get(dateStr) ?? []}
            />
            {daySlots.length === 0 ? (
              <div className="py-16 text-center border-2 border-dashed border-gray-200 rounded-xl">
                <Settings2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 mb-2">No timetable slots for this day</p>
                <p className="text-sm text-gray-400 mb-4">
                  {currentDate.getDay() === 0 || currentDate.getDay() === 6
                    ? "It's the weekend — enjoy the break!"
                    : isSupportStaff
                      ? "Set up the periods you'll be working today."
                      : "Configure your day structure in Timetable Settings."}
                </p>
                {currentDate.getDay() !== 0 && currentDate.getDay() !== 6 && (
                  isSupportStaff ? (
                    <Button className="gap-2" onClick={() => setOverrideOpen(true)}>
                      <Pencil className="h-4 w-4" />
                      Set up today's periods
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={() => setLocation("/app/timetable-settings")}>Edit Timetable</Button>
                  )
                )}
              </div>
            ) : (
              <>
                {daySlots.map((slot) => {
                  const slotKey = slot._slotKey ?? `slot-${slot.id}`;
                  // entryMap is pre-populated (above) so override-{n} keys
                  // already resolve to the base-timetable entry when no
                  // override-specific entry has been written yet. A direct
                  // get is all that's needed here.
                  const entry = entryMap.get(slotKey);
                  return (
                    <PeriodCard
                      key={slotKey}
                      slot={slot}
                      date={currentDate}
                      timetableId={activeTimetable.id}
                      slotKey={slotKey}
                      initialContent={entry?.content ?? ""}
                      initialDiaryEntryId={entry?.id ?? null}
                      records={recordsByPeriod.get(slot.label) ?? []}
                    />
                  );
                })}
                {/* Records for today that don't match any visible period slot */}
                {unmatchedRecords.length > 0 && (
                  <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                    <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Other records for today
                      </span>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {unmatchedRecords.map((r: any) => (
                        <InlineRecordChip key={r.id} record={r} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : view === "week" ? (
          <div className="max-w-7xl mx-auto overflow-x-auto">
            <div className="flex gap-4 min-w-[640px]">
              {weekDays.map((day) => {
                const dayKey = format(day, "yyyy-MM-dd");
                return (
                  <WeekDayColumn
                    key={day.toISOString()}
                    date={day}
                    slots={allSlots}
                    timetableId={activeTimetable.id}
                    dayNotes={weekDayNotesMap.get(dayKey) ?? ""}
                    events={dayEventsMap.get(dayKey) ?? []}
                    getSlotContent={(slotKey) => weekSlotContentMap.get(`${dayKey}_${slotKey}`) ?? ""}
                    override={weekOverridesByDate.get(dayKey)}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          <MonthView
            month={currentDate}
            timetable={activeTimetable ?? null}
            timetableDetail={timetableDetail ?? null}
            events={(calendarEvents as any[]) ?? []}
            onCreateEvent={async (date, title, color) => {
              await createEvent.mutateAsync({ data: { date, title, color } });
            }}
            onDeleteEvent={async (id) => {
              await deleteEvent.mutateAsync({ eventId: id });
            }}
            onUpdateEvent={async (id, title, color) => {
              await updateEvent.mutateAsync({ eventId: id, data: { title, color } });
            }}
            onNavigateToDay={(date) => {
              setCurrentDate(date);
              setView("day");
            }}
          />
        )}
      </div>
    </div>
  );
}
